import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useContentModerationLog } from '@/hooks/use-content';

// SPEC-021: Admin-only panel showing the full moderation audit trail for
// a single content item. Used in the editor page sidebar so admins can
// see the entire history (submit → approve → unpublish → …) at a glance.

const ACTION_LABELS: Record<string, string> = {
  submit: 'Submitted',
  approve: 'Approved',
  reject: 'Rejected',
  unpublish: 'Unpublished',
  archive: 'Archived',
  edit_after_publish: 'Edited after publish',
  delete: 'Deleted',
  restore: 'Restored',
};

const ACTION_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  submit: 'secondary',
  approve: 'default',
  reject: 'destructive',
  unpublish: 'outline',
  archive: 'outline',
  edit_after_publish: 'outline',
  delete: 'destructive',
  restore: 'secondary',
};

export function ModerationLogPanel({ contentId }: { contentId: string }) {
  const { data: entries, isLoading } = useContentModerationLog(contentId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Moderation history</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        )}
        {!isLoading && (!entries || entries.length === 0) && (
          <p className="text-sm text-muted-foreground">No history yet.</p>
        )}
        <ol className="space-y-2">
          {(entries ?? []).map((entry) => (
            <li
              key={entry.id}
              className="rounded-md border bg-muted/30 px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between">
                <Badge variant={ACTION_VARIANT[entry.action] ?? 'outline'}>
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </Badge>
                <span className="text-muted-foreground">
                  {new Date(entry.created_at).toLocaleString()}
                </span>
              </div>
              {entry.previous_status && entry.new_status && (
                <p className="mt-1 text-muted-foreground">
                  {entry.previous_status} → {entry.new_status}
                </p>
              )}
              {entry.reason && (
                <p className="mt-1 italic text-muted-foreground">“{entry.reason}”</p>
              )}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
